import { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';

import TransaccionTopbar from './components/TransaccionTopbar';
import TransaccionStats from './components/TransaccionStats';
import TransaccionFilter from './components/TransaccionFilter';
import TransaccionesTable from './components/TransaccionesTable';
import TransaccionDetailDrawer from './components/TransaccionDetailDrawer';

// ----------------------------------------------------------------------
// Interfaces del API
// ----------------------------------------------------------------------
export interface SummaryReport {
  totalEarning: number;
  transactionsCount: number;
  totalRefund: number;
  totalJomCommission: number;
  currencyCode: string;
}

export interface TransactionGuest {
  fullName: string;
  initials: string;
  avatarColor: string;
}

export interface TransactionItem {
  payoutTransactionId: number;
  transactionCode: string;
  createdAt: string;
  transactionStatus: string;
  transacionStatusId: number;
  amount: number;
  commission: number;
  listingRentId: number;
  propertyName: string;
  guest: TransactionGuest;
}

// Funciones para calcular fechas dinámicas
const getFirstDayOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};
const getCurrentDay = () => {
  return new Date().toISOString().split('T')[0];
};

export const TransaccionesPageNew = () => {
  // --- ESTADOS DE UI ---
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // --- ESTADOS DE DATOS ---
  const [summary, setSummary] = useState<SummaryReport | null>(null);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);

  // --- ESTADOS DE FILTROS ---
  const [filters, setFilters] = useState({
    startDate: getFirstDayOfMonth(),
    endDate: getCurrentDay(),
    statusName: 'TODAS',
    guestName: '',
    transactionCode: ''
  });

  // ----------------------------------------------------------------------
  // LLAMADA A LA API
  // ----------------------------------------------------------------------
  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("startDate", filters.startDate);
      params.append("endDate", filters.endDate);

      if (filters.statusName !== 'TODAS') {
        params.append("statusName", filters.statusName);
      }
      if (filters.guestName) params.append("guestName", filters.guestName);
      if (filters.transactionCode) params.append("transactionCode", filters.transactionCode);

      const endpoint = `GetTransferTransactonReportAsync?${params.toString()}`;
      const response = await apiClient.get<any>(endpoint);

      const resBody = response.data ? response.data : response;

      const actualData = resBody.data ? resBody.data : resBody;

      setSummary(actualData.sumaryReport || null);
      setTransactions(actualData.transations || []);

    } catch (error) {
      console.error("Error al obtener reporte de transacciones:", error);
      setSummary(null);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [filters]);

  // ----------------------------------------------------------------------
  // HANDLERS
  // ----------------------------------------------------------------------
  const handleOpenDrawer = (transaction: TransactionItem) => {
    setSelectedTransaction(transaction);
    setIsDrawerOpen(true);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="relative flex flex-col w-full h-screen overflow-hidden font-sans text-gray-900 bg-gray-50">
      <div className="flex flex-col flex-1 w-full h-full overflow-y-auto overflow-x-hidden hide-scrollbar">

        <TransaccionTopbar />

        <TransaccionStats summary={summary} isLoading={isLoading} />

        <TransaccionFilter
          filters={filters}
          onFilterChange={handleFilterChange}
        />

        <TransaccionesTable
          transactions={transactions}
          isLoading={isLoading}
          onOpenDrawer={handleOpenDrawer}
        />

      </div>

      <TransaccionDetailDrawer
        isOpen={isDrawerOpen}
        transaction={selectedTransaction}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
};

export default TransaccionesPageNew;